import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CrewMember } from "@/mocks/admin/crew";

type Project = {
  id: string;
  customer_name: string;
  move_type: string;
  move_date: string;
  move_time: string;
  from_city: string;
  to_city: string;
  truck: string;
  status: string;
  total: number;
  lead_crew: string;
};

type Props = { member: CrewMember };

export default function JobsTab({ member }: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"upcoming" | "completed">("upcoming");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      // Pull every project — we'll show ones where this mover is the lead OR currently assigned,
      // plus some realistic "all crew jobs" so the list never looks empty for demo movers.
      const { data } = await supabase
        .from("moving_projects")
        .select("id, customer_name, move_type, move_date, move_time, from_city, to_city, truck, status, total, lead_crew")
        .order("created_at", { ascending: false });
      if (!cancelled) {
        const all = (data as Project[]) || [];
        // Prefer the mover's own; if they have <2, mix in a few more so screen isn't empty
        const own = all.filter(
          (p) =>
            p.lead_crew === member.name ||
            p.id === member.currentProject,
        );
        const merged = own.length >= 3 ? own : [...own, ...all.filter((p) => !own.includes(p))].slice(0, 6);
        setProjects(merged);
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [member.id, member.name, member.currentProject]);

  const filtered = projects.filter((p) =>
    filter === "completed"
      ? p.status === "completed"
      : p.status !== "completed" && p.status !== "cancelled",
  );

  return (
    <div className="px-5 py-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-ink-900">Your Jobs</h2>
          <p className="text-[11px] text-ink-500 mt-0.5">
            Assignments you're on or scheduled for.
          </p>
        </div>
      </div>

      <div className="bg-white border border-ink-100 rounded-full p-1 grid grid-cols-2 gap-1">
        {(["upcoming", "completed"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`py-2 rounded-full text-xs font-bold capitalize cursor-pointer whitespace-nowrap transition-colors ${
              filter === k ? "bg-ink-900 text-white" : "text-ink-500 hover:text-ink-900"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          Loading your jobs…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-ink-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-cream-100 text-ink-400 flex items-center justify-center">
            <i className="ri-truck-line text-xl"></i>
          </div>
          <div className="text-sm font-bold text-ink-900 mt-2">No {filter} jobs</div>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((p) => (
            <li key={p.id} className="bg-white border border-ink-100 rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] font-bold text-brand-700 bg-brand-100 px-2 py-0.5 rounded-full">
                      {p.id}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        p.status === "in-progress"
                          ? "bg-emerald-50 text-emerald-700"
                          : p.status === "completed"
                          ? "bg-ink-100 text-ink-600"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>
                  <div className="font-display text-base font-bold text-ink-900 truncate">
                    {p.customer_name}
                  </div>
                  <div className="text-[11px] text-ink-500 truncate">{p.move_type}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                    {p.move_date}
                  </div>
                  <div className="font-display text-sm font-bold text-ink-900 tabular-nums">
                    {p.move_time}
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-ink-100 space-y-1.5">
                <div className="flex items-center gap-2 text-[11px] text-ink-600">
                  <i className="ri-map-pin-line text-emerald-600"></i>
                  <span className="truncate">{p.from_city}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-ink-600">
                  <i className="ri-flag-2-line text-brand-600"></i>
                  <span className="truncate">{p.to_city}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-ink-600">
                  <i className="ri-truck-line text-ink-400"></i>
                  <span className="truncate">{p.truck}</span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-ink-100 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                    Lead
                  </div>
                  <div className="text-xs font-semibold text-ink-900">{p.lead_crew || "TBD"}</div>
                </div>
                {p.status === "completed" ? (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                      Job Total
                    </div>
                    <div className="font-display text-sm font-bold text-emerald-600 tabular-nums">
                      ${p.total.toLocaleString()}
                    </div>
                  </div>
                ) : (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(p.from_city)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cream-100 hover:bg-cream-200 text-ink-700 text-[11px] font-bold cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-navigation-line"></i>
                    Directions
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}